import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../App.css';

import { API_URL } from '../config';

const headerStyles = {
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
};

function TabelasExplorer() {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [hideEmptyTables, setHideEmptyTables] = useState(true);
  const [recordLimit, setRecordLimit] = useState(500);

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/tables`);
      setTables(response.data);
    } catch (err) {
      setError('Erro ao buscar tabelas: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTableSelect = async (tableName, limit = recordLimit) => {
    setSelectedTable(tableName);
    setLoading(true);
    setError('');
    try {
      const [dataResponse, columnsResponse] = await Promise.all([
        axios.get(`${API_URL}/table/${tableName}?limit=${limit}`),
        axios.get(`${API_URL}/table/${tableName}/columns`)
      ]);
      setTableData(dataResponse.data);
      setColumns(columnsResponse.data);
    } catch (err) {
      setError('Erro ao buscar dados: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLimitChange = async (newLimit) => {
    setRecordLimit(newLimit);
    if (selectedTable) {
      await handleTableSelect(selectedTable, newLimit);
    }
  };

  const handleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  const getSortedTables = () => {
    let filtered = tables;

    if (hideEmptyTables) {
      filtered = tables.filter(table => table.RECORD_COUNT > 0);
    }

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'name') {
        const comparison = a.TABLE_NAME.localeCompare(b.TABLE_NAME);
        return sortOrder === 'asc' ? comparison : -comparison;
      } else {
        const comparison = a.RECORD_COUNT - b.RECORD_COUNT;
        return sortOrder === 'asc' ? comparison : -comparison;
      }
    });
    return sorted;
  };

  return (
    <div className="App">
      <div className="container">
        <div className="sidebar">
          <div className="sidebar-header">
            <h2>Tabelas</h2>
            <div className="sort-buttons">
              <button
                className={sortBy === 'name' ? 'active' : ''}
                onClick={() => handleSort('name')}
                title="Ordenar por nome"
              >
                Nome {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button
                className={sortBy === 'count' ? 'active' : ''}
                onClick={() => handleSort('count')}
                title="Ordenar por quantidade de registros"
              >
                Qtd {sortBy === 'count' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
            </div>
            <div className="filter-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={hideEmptyTables}
                  onChange={(e) => setHideEmptyTables(e.target.checked)}
                />
                <span>Ocultar tabelas vazias</span>
              </label>
            </div>
          </div>
          {loading && !selectedTable && <p>Carregando...</p>}
          {error && !selectedTable && <p className="error">{error}</p>}
          <ul className="table-list">
            {getSortedTables().map((table) => (
              <li
                key={table.TABLE_NAME}
                className={selectedTable === table.TABLE_NAME ? 'active' : ''}
                onClick={() => handleTableSelect(table.TABLE_NAME)}
              >
                <div className="table-item">
                  <span className="table-name">{table.TABLE_NAME}</span>
                  <span className="table-count">{table.RECORD_COUNT.toLocaleString()}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="main-content">
          {selectedTable ? (
            <>
              <div style={headerStyles.pageHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={headerStyles.pageIcon}>🗄️</span>
                  <div>
                    <h1 style={headerStyles.pageTitle}>Explorador de Tabelas</h1>
                    <p style={headerStyles.pageSubtitle}>Tabela selecionada: {selectedTable}</p>
                  </div>
                </div>
                <div className="limit-selector">
                  <label htmlFor="recordLimit">Registros:</label>
                  <select
                    id="recordLimit"
                    value={recordLimit}
                    onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                  >
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                    <option value={1000}>1.000</option>
                    <option value={5000}>5.000</option>
                    <option value={10000}>10.000</option>
                    <option value={50000}>50.000</option>
                  </select>
                </div>
              </div>

              {columns.length > 0 && (
                <div className="columns-info">
                  <h3>Estrutura da Tabela</h3>
                  <table className="info-table">
                    <thead>
                      <tr>
                        <th>Coluna</th>
                        <th>Tipo</th>
                        <th>Tamanho</th>
                        <th>Nulo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {columns.map((col, idx) => (
                        <tr key={idx}>
                          <td>{col.COLUMN_NAME}</td>
                          <td>{col.DATA_TYPE}</td>
                          <td>{col.CHARACTER_MAXIMUM_LENGTH || '-'}</td>
                          <td>{col.IS_NULLABLE}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {loading ? (
                <p>Carregando dados...</p>
              ) : error ? (
                <p className="error">{error}</p>
              ) : tableData.length > 0 ? (
                <div className="table-data">
                  <h3>Dados</h3>
                  <div className="table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr>
                          {Object.keys(tableData[0]).map((key) => (
                            <th key={key}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.map((row, idx) => (
                          <tr key={idx}>
                            {Object.values(row).map((value, i) => (
                              <td key={i}>
                                {value === null ? 'NULL' : String(value)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="record-count">Total de registros exibidos: {tableData.length}</p>
                </div>
              ) : (
                <p>Nenhum dado encontrado nesta tabela.</p>
              )}
            </>
          ) : (
            <div className="welcome">
              <h2>Bem-vindo ao Explorador de Tabelas!</h2>
              <p>Selecione uma tabela à esquerda para visualizar seus dados.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TabelasExplorer;
